import { Check, ChevronsUpDown, Layers, Plus, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { ProblemType } from '@/db/schema/enums'
import { PROBLEM_TAG_VOCABULARY } from '@/lib/constants/problem-tags'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { Badge } from '../shared/badge'
import { Modal } from '../shared/modal'
import { DIFFICULTIES, DIFFICULTY_LABELS } from './constants'
import type { CoachingQuestion } from './types'

const TYPE_OPTIONS: { value: ProblemType; label: string }[] = [
  { value: 'algorithms', label: 'Algorithms' },
  { value: 'sql', label: 'SQL' },
  { value: 'shell', label: 'Shell' },
]

interface QuestionFormData {
  externalId: string
  name: string
  difficulty: string
  premiumRequired: boolean
  type: ProblemType
  problemTagSlugs: string[]
  acceptanceRate: string
  freqBar: string
  totalAccepted: string
  totalSubmitted: string
  companies: string[]
  url: string
  editorialUrl: string
  source: string
  sourceIcon: string
}

interface EditQuestionModalProps {
  isOpen: boolean
  onClose: () => void
  question: CoachingQuestion | null
  onSubmit: (
    id: string,
    data: {
      externalId?: string
      name: string
      difficulty?: string | null
      premiumRequired?: boolean
      type?: ProblemType
      problemTagSlugs?: string[]
      acceptanceRate?: number | null
      freqBar?: number | null
      totalAccepted?: number | null
      totalSubmitted?: number | null
      companies?: string[]
      url?: string | null
      editorialUrl?: string | null
      source: string
      sourceIcon?: string | null
    },
  ) => Promise<void>
  sources: string[]
  onAddSource: (source: string) => void
  onEditApproaches?: () => void
}

function normalizeFormData(question: CoachingQuestion): QuestionFormData {
  return {
    externalId: question.externalId || '',
    name: question.name || '',
    difficulty: question.difficulty?.name || '',
    premiumRequired: question.premiumRequired || false,
    type: question.type || 'algorithms',
    problemTagSlugs: (question.problemTags || []).map((t) => t.slug),
    acceptanceRate: question.acceptanceRate?.toString() || '',
    freqBar: question.freqBar?.toString() || '',
    totalAccepted: question.totalAccepted?.toString() || '',
    totalSubmitted: question.totalSubmitted?.toString() || '',
    companies: question.companies || [],
    url: question.url || '',
    editorialUrl: question.editorialUrl || '',
    source: question.source || '',
    sourceIcon: question.sourceIcon || '',
  }
}

export function EditQuestionModal({
  isOpen,
  onClose,
  question,
  onSubmit,
  sources,
  onAddSource,
  onEditApproaches,
}: EditQuestionModalProps) {
  const [formData, setFormData] = useState<QuestionFormData>({
    externalId: '',
    name: '',
    difficulty: '',
    premiumRequired: false,
    type: 'algorithms',
    problemTagSlugs: [],
    acceptanceRate: '',
    freqBar: '',
    totalAccepted: '',
    totalSubmitted: '',
    companies: [],
    url: '',
    editorialUrl: '',
    source: '',
    sourceIcon: '',
  })
  const [problemTagInput, setProblemTagInput] = useState('')
  const { data: companiesData } = trpc.admin.companies.list.useQuery(
    undefined,
    { enabled: isOpen },
  )
  const companies = companiesData?.data ?? []
  const [companiesOpen, setCompaniesOpen] = useState(false)
  const [showNewSource, setShowNewSource] = useState(false)
  const [newSource, setNewSource] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Initialize form data when question changes
  useEffect(() => {
    if (question) {
      setFormData(normalizeFormData(question))
    }
  }, [question])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target
    const checked =
      type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleAddProblemTag = (slug: string) => {
    if (slug && !formData.problemTagSlugs.includes(slug)) {
      setFormData((prev) => ({
        ...prev,
        problemTagSlugs: [...prev.problemTagSlugs, slug],
      }))
      setProblemTagInput('')
    }
  }

  const handleRemoveProblemTag = (slug: string) => {
    setFormData((prev) => ({
      ...prev,
      problemTagSlugs: prev.problemTagSlugs.filter((s) => s !== slug),
    }))
  }

  const handleSelectCompany = (companySlug: string) => {
    if (companySlug && !formData.companies.includes(companySlug)) {
      setFormData((prev) => ({
        ...prev,
        companies: [...prev.companies, companySlug],
      }))
    }
    // Keep dropdown open for multiple selections
  }

  const handleRemoveCompany = (companySlug: string) => {
    setFormData((prev) => ({
      ...prev,
      companies: prev.companies.filter((slug) => slug !== companySlug),
    }))
  }

  const handleAddNewSource = () => {
    if (newSource.trim()) {
      onAddSource(newSource.trim())
      setFormData((prev) => ({ ...prev, source: newSource.trim() }))
      setNewSource('')
      setShowNewSource(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!question) return

    if (!formData.name.trim() || !formData.source) {
      setError('Name and Source are required')
      return
    }

    setLoading(true)
    try {
      await onSubmit(question.id, {
        externalId: formData.externalId || undefined,
        name: formData.name,
        difficulty: formData.difficulty || null,
        premiumRequired: formData.premiumRequired,
        type: formData.type,
        problemTagSlugs: formData.problemTagSlugs,
        acceptanceRate: formData.acceptanceRate
          ? Number.parseFloat(formData.acceptanceRate)
          : null,
        freqBar: formData.freqBar ? Number.parseFloat(formData.freqBar) : null,
        totalAccepted: formData.totalAccepted
          ? Number.parseInt(formData.totalAccepted, 10)
          : null,
        totalSubmitted: formData.totalSubmitted
          ? Number.parseInt(formData.totalSubmitted, 10)
          : null,
        companies: formData.companies,
        url: formData.url || null,
        editorialUrl: formData.editorialUrl || null,
        source: formData.source,
        sourceIcon: formData.sourceIcon || null,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update question')
    } finally {
      setLoading(false)
    }
  }

  if (!question) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Problem" size="wide">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-destructive/20 border-destructive/30 text-destructive rounded-lg border p-3 text-sm">
            {error}
          </div>
        )}

        {/* Row 1: Name, ID, Difficulty */}
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3">
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Name <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Question name"
              required
            />
          </div>
          <div className="col-span-2">
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              ID
            </label>
            <Input
              type="text"
              name="externalId"
              value={formData.externalId}
              onChange={handleChange}
              placeholder="e.g., 1"
              className="font-mono text-sm"
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Difficulty
            </label>
            <select
              name="difficulty"
              value={formData.difficulty}
              onChange={handleChange}
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
            >
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {DIFFICULTY_LABELS[d]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 2: Source and URLs */}
        <div className="grid grid-cols-[1fr_2fr_2fr] gap-6">
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Source <span className="text-destructive">*</span>
            </label>
            {showNewSource ? (
              <div className="flex gap-1">
                <Input
                  type="text"
                  value={newSource}
                  onChange={(e) => setNewSource(e.target.value)}
                  placeholder="New source"
                  className="flex-1"
                />
                <Button type="button" onClick={handleAddNewSource} size="sm">
                  Add
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNewSource(false)}
                >
                  <X size={14} />
                </Button>
              </div>
            ) : (
              <div className="flex gap-1">
                <select
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  className="border-input bg-background h-9 flex-1 rounded-md border px-3 text-sm"
                  required
                >
                  <option value="">Select source</option>
                  {sources.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewSource(true)}
                >
                  <Plus size={14} />
                </Button>
              </div>
            )}
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Problem URL
            </label>
            <Input
              type="url"
              name="url"
              value={formData.url}
              onChange={handleChange}
              placeholder="https://leetcode.com/problems/..."
              autoComplete="off"
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Editorial URL
            </label>
            <Input
              type="url"
              name="editorialUrl"
              value={formData.editorialUrl}
              onChange={handleChange}
              placeholder="Editorial link"
              autoComplete="off"
            />
          </div>
        </div>

        {/* Row 3: Stats */}
        <div className="grid grid-cols-5 gap-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Accept %
            </label>
            <Input
              type="text"
              name="acceptanceRate"
              value={
                formData.acceptanceRate
                  ? `${Number.parseFloat(formData.acceptanceRate).toFixed(2)}%`
                  : '—'
              }
              readOnly
              disabled
              className="bg-muted cursor-not-allowed"
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Freq Bar
            </label>
            <Input
              type="number"
              name="freqBar"
              value={formData.freqBar}
              onChange={handleChange}
              placeholder="93.79"
              step="0.1"
              min="0"
              max="100"
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Accepted
            </label>
            <Input
              type="number"
              name="totalAccepted"
              value={formData.totalAccepted}
              onChange={handleChange}
              placeholder="1699077"
              min="0"
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Submitted
            </label>
            <Input
              type="number"
              name="totalSubmitted"
              value={formData.totalSubmitted}
              onChange={handleChange}
              placeholder="1901107"
              min="0"
            />
          </div>
          <div className="flex items-end">
            <label className="flex h-9 cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                name="premiumRequired"
                checked={formData.premiumRequired}
                onChange={handleChange}
                className="rounded"
              />
              <span className="text-muted-foreground text-sm">Premium</span>
            </label>
          </div>
        </div>

        {/* Row 4: Type + Problem Tags */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Type
            </label>
            <div className="flex gap-2">
              {TYPE_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  variant={
                    formData.type === option.value ? 'default' : 'outline'
                  }
                  size="sm"
                  className="flex-1"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, type: option.value }))
                  }
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Problem Tags
            </label>
            <div className="flex gap-2">
              <select
                value={problemTagInput}
                onChange={(e) => setProblemTagInput(e.target.value)}
                className="border-input bg-background h-9 flex-1 rounded-md border px-3 text-sm"
              >
                <option value="">Select tag</option>
                {PROBLEM_TAG_VOCABULARY.filter(
                  (t) => !formData.problemTagSlugs.includes(t.slug),
                ).map((t) => (
                  <option key={t.slug} value={t.slug}>
                    {t.name}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  if (problemTagInput) {
                    handleAddProblemTag(problemTagInput)
                  }
                }}
                disabled={!problemTagInput}
              >
                Add
              </Button>
            </div>
          </div>
          {formData.problemTagSlugs.length > 0 && (
            <div className="col-span-2 flex flex-wrap gap-1">
              {formData.problemTagSlugs.map((slug) => {
                const tag = PROBLEM_TAG_VOCABULARY.find((t) => t.slug === slug)
                return (
                  <Badge key={slug} variant="topic" className="gap-1 pr-1">
                    {tag?.name || slug}
                    <button
                      type="button"
                      onClick={() => handleRemoveProblemTag(slug)}
                      className="hover:text-foreground ml-1"
                    >
                      <X size={12} />
                    </button>
                  </Badge>
                )
              })}
            </div>
          )}
        </div>

        {/* Row 5: Companies */}
        <div>
          <label className="text-muted-foreground mb-1 flex items-center gap-2 text-sm font-medium">
            Companies
            <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs font-normal">
              {companies.length} total
            </span>
          </label>
          <Popover open={companiesOpen} onOpenChange={setCompaniesOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={companiesOpen}
                className="w-full justify-between font-normal"
              >
                Search companies...
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[400px] p-0"
              align="start"
              side="bottom"
              avoidCollisions={false}
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <Command shouldFilter={true}>
                <CommandInput placeholder="Search by company name..." />
                <CommandList className="max-h-[300px]">
                  <CommandEmpty>No company found.</CommandEmpty>
                  <CommandGroup>
                    {companies
                      .filter((c) => !formData.companies.includes(c.slug))
                      .map((company) => (
                        <CommandItem
                          key={company.id}
                          value={`${company.name} ${company.slug}`}
                          onSelect={() => handleSelectCompany(company.slug)}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              formData.companies.includes(company.slug)
                                ? 'opacity-100'
                                : 'opacity-0',
                            )}
                          />
                          <span className="truncate">{company.name}</span>
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {formData.companies.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {formData.companies.map((companySlug) => {
                const company = companies.find((c) => c.slug === companySlug)
                return (
                  <Badge
                    key={companySlug}
                    variant="topic"
                    className="gap-1 pr-1 text-xs"
                  >
                    {company?.name || companySlug}
                    <button
                      type="button"
                      onClick={() => handleRemoveCompany(companySlug)}
                      className="hover:text-foreground ml-0.5"
                    >
                      <X size={10} />
                    </button>
                  </Badge>
                )
              })}
            </div>
          )}
        </div>

        <div className="border-border flex items-center justify-between border-t pt-4">
          {onEditApproaches ? (
            <Button
              type="button"
              variant="outline"
              onClick={onEditApproaches}
              className="gap-1"
            >
              <Layers size={14} />
              Edit Approaches
              {question?.approaches && question.approaches.length > 0 && (
                <span className="bg-muted text-muted-foreground ml-1 rounded px-1.5 py-0.5 text-xs">
                  {question.approaches.length}
                </span>
              )}
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-3">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
